(() => {
  "use strict";

  if (window.__ecomificCartReconcileInit) return;
  window.__ecomificCartReconcileInit = true;

  const SELECTORS = {
    form: "#CartPageForm",
    itemsContainer: "#CartPageForm [data-products]",
    checkoutButton: '#CartPageForm button[name="checkout"]',
    acceleratedCheckout: "#CartPageForm .additional-checkout-buttons",
    panel: "[data-ecomific-cart-reconcile]",
    panelStack: "[data-ecomific-cart-reconcile-stack]",
    cartItemRow: ".cart__item[data-key]",
    removeLink: '.cart__remove a[href*="/cart/change"]',
    noticeClose: "[data-ecomific-notice-close]",
  };

  const ERROR_MESSAGE =
    "We couldn’t verify regional availability right now. Please refresh and try again.";
  const CART_UPDATED_MESSAGE =
    "We’ve updated your cart based on availability in your shipping region.";
  const CART_UPDATED_MISMATCH_MESSAGE =
    "We have adjusted your cart to reflect the inventory levels in this region.";
  const CART_EMPTIED_MESSAGE =
    "The items in your cart are not available in this shipping region, so your cart is now empty.";
  const REMOVED_REASON = "Not available in your region.";
  const REDUCED_REASON_PREFIX = "Only ";
  const REDUCED_REASON_SUFFIX = " available in your region.";
  const SMARTCART_BANNER_STICKY_MS = 10000;
  const RECONCILE_MESSAGE_MIN_VISIBLE_MS = 5000;
  const SMARTCART_TRANSIENT_MESSAGE_MS = 5000;
  const SMARTCART_ACTION_TIMEOUT_MS = 3500;
  const CART_ADJUSTMENT_SESSION_KEY = "ecomificCartAdjustment";
  const CART_UI_SYNC_DEBOUNCE_MS = 180;
  const REGION_TRIGGER_WINDOW_MS = 2500;

  const CHECKOUT_BLOCKED_ATTR = "ecomificBlocked";
  const CHECKOUT_PREV_DISABLED_ATTR = "ecomificPrevDisabled";
  const ACCEL_PREV_DISPLAY_ATTR = "ecomificPrevDisplay";
  const RUN_DEBOUNCE_MS = 200;
  const RUN_DEBOUNCE_AFTER_ADJUST_MS = 750;
  const CONCURRENCY_LIMIT = 3;
  const LOG_NS = "ecomific";
  const MODULE = "cart-reconcile";

  function isDebugEnabled() {
    if (typeof window === "undefined") return false;
    if (window.ecomificDebug === true || window.__ecomificDebug === true) {
      return true;
    }

    try {
      if (window.localStorage?.getItem?.("ecomific_debug") === "1") {
        return true;
      }
    } catch (err) {}

    try {
      if (window.location?.search) {
        const params = new URLSearchParams(window.location.search);
        if (params.has("ecomificDebug")) {
          return true;
        }
      }
    } catch (err) {}

    return false;
  }

  const DEBUG_CART_RECONCILE = isDebugEnabled();

  function fmt(level, msg) {
    return `[${LOG_NS}][${level}] ${MODULE}: ${msg}`;
  }

  function logDebug(msg, ...args) {
    if (!DEBUG_CART_RECONCILE) return;
    console.debug(fmt("debug", msg), ...args);
  }

  function logInfo(msg, ...args) {
    if (!DEBUG_CART_RECONCILE) return;
    console.info(fmt("info", msg), ...args);
  }

  function logWarn(msg, ...args) {
    console.warn(fmt("warn", msg), ...args);
  }

  function logError(msg, ...args) {
    console.error(fmt("error", msg), ...args);
  }

  let _cartMutationSnifferPatched = false;

  function patchCartMutationSniffer() {
    if (_cartMutationSnifferPatched) return;
    _cartMutationSnifferPatched = true;

    // Patch fetch
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const url = String(args?.[0] || "");
      const isCartMutation =
        url.includes("/cart/add.js") ||
        url.includes("/cart/change.js") ||
        url.includes("/cart/update.js");

      if (DEBUG_CART_RECONCILE && isCartMutation) {
        const stack = new Error().stack;
        logDebug("fetch", { url, args, stack });
      }

      const res = await originalFetch.apply(this, args);

      if (DEBUG_CART_RECONCILE && isCartMutation) {
        try {
          const clone = res.clone();
          const text = await clone.text();
          logDebug("fetch:response", {
            url,
            status: res.status,
            body: text,
          });
        } catch (e) {
          logDebug("fetch:response:unreadable", {
            url,
            status: res.status,
            err: e,
          });
        }
      }

      return res;
    };

    // Patch XHR as well (some libs still use it)
    const OriginalXHR = window.XMLHttpRequest;
    function PatchedXHR() {
      const xhr = new OriginalXHR();
      let _url = "";
      const originalOpen = xhr.open;
      xhr.open = function (method, url, ...rest) {
        _url = String(url || "");
        return originalOpen.call(this, method, url, ...rest);
      };

      xhr.addEventListener("loadend", function () {
        const isCartMutation =
          _url.includes("/cart/add.js") ||
          _url.includes("/cart/change.js") ||
          _url.includes("/cart/update.js");

        if (!DEBUG_CART_RECONCILE || !isCartMutation) return;

        logDebug("xhr", {
          url: _url,
          status: xhr.status,
          response: xhr.responseText,
          stack: new Error().stack,
        });
      });

      return xhr;
    }
    window.XMLHttpRequest = PatchedXHR;
  }

  let runId = 0;
  let currentController = null;
  let debounceId = null;
  let initLogged = false;
  let lastCartActions = [];
  let lastRunSummary = null;
  let smartCartBannerHideTimer = null;
  let smartCartTransientHideTimer = null;
  let smartCartBannerHover = false;
  let smartCartObserverBound = false;
  let _ecomificAlertPatched = false;
  let runInFlight = false;
  let pendingRun = false;
  let panelMinVisibleUntil = 0;
  let panelDeferredClearTimer = null;
  let cartUiSyncDebounceId = null;
  let lastRegionTriggerAt = 0;
  let panelCloseBound = false;
  let smartCartNoticeId = 0;
  let smartCartNotices = [];
  let lastSmartCartIncreaseIntent = null;
  let smartCartIncreaseIntentTimer = null;
  let lastCartNoticeContext = null;

  function patchCartAlert() {
    if (_ecomificAlertPatched) return;
    _ecomificAlertPatched = true;

    const original = window.alert;
    window.alert = function (msg) {
      const text = String(msg || "");
      const normalized = text.toLowerCase();
      const isInventoryLimitAlert =
        isQuantityLimitAlertMessage(normalized) ||
        isUnavailableAlertMessage(normalized);

      if (isInventoryLimitAlert) {
        const normalizedMessage = normalizeInventoryAlertMessage(text);
        // Suppress native browser alerts for cart quantity/stock errors.
        // On /cart, render inline panel copy instead.
        // On smartcart surfaces, render into the adjustment banner slot.
        if (document.querySelector(SELECTORS.panel)) {
          showBanner(normalizedMessage);
        } else {
          showSmartCartTransientMessage(
            normalizedMessage,
            SMARTCART_TRANSIENT_MESSAGE_MS
          );
        }
        return;
      }
      return original.call(window, msg);
    };
  }
  const dlog = (...args) => {
    if (!DEBUG_CART_RECONCILE) return;
    logDebug("trace", ...args);
  };

  function isQuantityLimitAlertMessage(normalized) {
    const englishMatch =
      normalized.includes("only") &&
      (normalized.includes("available") ||
        normalized.includes("left") ||
        normalized.includes("were added to your cart"));
    const frenchMatch =
      (normalized.includes("seulement") ||
        normalized.includes("ajout") ||
        normalized.includes("ajouté")) &&
      (normalized.includes("disponibilit") ||
        normalized.includes("disponible")) &&
      normalized.includes("panier");
    return englishMatch || frenchMatch;
  }

  function isUnavailableAlertMessage(normalized) {
    return (
      normalized.includes("out of stock") ||
      normalized.includes("sold out") ||
      normalized.includes("can't add") ||
      normalized.includes("cannot add") ||
      normalized.includes("rupture de stock") ||
      normalized.includes("épuisé") ||
      normalized.includes("epuise") ||
      normalized.includes("impossible d'ajouter") ||
      normalized.includes("impossible d’ajouter") ||
      normalized.includes("ne peut pas être ajouté") ||
      normalized.includes("ne peut pas etre ajoute")
    );
  }

  function extractAlertQuantity(raw) {
    const englishMatch = raw.match(/only\s+(\d+)/i);
    if (englishMatch) return Number(englishMatch[1] || 0);
    const leadingMatch = raw.match(/^\s*(\d+)\b/);
    if (leadingMatch) return Number(leadingMatch[1] || 0);
    return 0;
  }

  function normalizeInventoryAlertMessage(text) {
    const raw = String(text || "").trim();
    if (!raw) return getMessageConfig().inventoryAdjusted;
    const normalized = raw.toLowerCase();
    const productTitle = consumeCartNoticeProductTitle();
    const quantity = extractAlertQuantity(raw);
    if (quantity > 0 && isQuantityLimitAlertMessage(normalized)) {
      return buildQuantityLimitedMessage(
        quantity,
        productTitle
      );
    }
    if (isUnavailableAlertMessage(normalized)) {
      return getMessageConfig().unavailable;
    }
    return raw;
  }

  function captureCartNoticeContext(target) {
    if (!target || !target.closest) return;
    const row = target.closest(SELECTORS.cartItemRow);
    if (!row) return;
    const productTitle = normalizeWhitespace(
      row.querySelector(".cart__item-name")?.textContent || ""
    );
    if (!productTitle) return;
    lastCartNoticeContext = {
      productTitle,
      clickedAt: Date.now(),
    };
  }

  function consumeCartNoticeProductTitle() {
    const context = lastCartNoticeContext;
    lastCartNoticeContext = null;
    if (!context) return "";
    if (Date.now() - Number(context.clickedAt || 0) > 3000) {
      return "";
    }
    return normalizeWhitespace(context.productTitle || "");
  }

  function captureSmartCartIncreaseIntent(target) {
    if (!target || !target.closest) return;
    const button = target.closest(
      "#rebuy-cart .rebuy-cart__flyout-item-quantity-widget-button"
    );
    if (!button) return;
    const label = String(
      button.getAttribute("aria-label") || button.getAttribute("alt") || ""
    ).toLowerCase();
    if (!label.startsWith("increase quantity")) return;
    const itemEl = button.closest(".rebuy-cart__flyout-item");
    if (!itemEl) return;
    const itemEls = Array.from(
      document.querySelectorAll("#rebuy-cart .rebuy-cart__flyout-item")
    );
    const rowIndex = itemEls.indexOf(itemEl);
    if (rowIndex < 0) return;
    const qtyInput = itemEl.querySelector(
      ".rebuy-cart__flyout-item-quantity-widget-label"
    );
    const quantityBefore = Math.max(
      0,
      Number(qtyInput?.value || qtyInput?.getAttribute("value") || 0)
    );
    if (!quantityBefore) return;
    const productTitle = normalizeWhitespace(
      itemEl.querySelector(".rebuy-cart__flyout-item-product-title")?.textContent || ""
    );
    const smartCart = getSmartCart();
    const items = Array.isArray(smartCart?.items?.()) ? smartCart.items() : [];
    const variantId = getSmartCartVariantId(items[rowIndex]);
    lastSmartCartIncreaseIntent = {
      rowIndex,
      variantId,
      productTitle,
      quantityBefore,
      desiredQty: quantityBefore + 1,
      clickedAt: Date.now(),
    };
    if (smartCartIncreaseIntentTimer) {
      clearTimeout(smartCartIncreaseIntentTimer);
    }
    smartCartIncreaseIntentTimer = setTimeout(() => {
      verifySmartCartIncreaseIntent();
    }, 900);
  }

  function clearSmartCartIncreaseIntent() {
    if (smartCartIncreaseIntentTimer) {
      clearTimeout(smartCartIncreaseIntentTimer);
      smartCartIncreaseIntentTimer = null;
    }
    lastSmartCartIncreaseIntent = null;
  }

  function clearSmartCartNotices() {
    smartCartNotices = [];
    renderSmartCartNotices();
    clearSmartCartIncreaseIntent();
  }

  async function verifySmartCartIncreaseIntent() {
    const intent = lastSmartCartIncreaseIntent;
    if (!intent) return;
    if (Date.now() - intent.clickedAt > 3000) {
      clearSmartCartIncreaseIntent();
      return;
    }
    const smartCart = getSmartCart();
    const items = Array.isArray(smartCart?.items?.()) ? smartCart.items() : [];
    const item = items[intent.rowIndex];
    if (!item) {
      clearSmartCartIncreaseIntent();
      return;
    }
    const currentQty = Math.max(0, Number(item.quantity || 0));
    if (currentQty <= 0) {
      clearSmartCartIncreaseIntent();
      return;
    }
    if (currentQty > intent.quantityBefore) {
      clearSmartCartIncreaseIntent();
      return;
    }

    const endpoint = buildInventoryEndpoint(getEndpointBase());
    const region = getCurrentRegionSafe();
    const variantId = intent.variantId || getSmartCartVariantId(item);
    const availability =
      endpoint && region && variantId
        ? await fetchInventory(endpoint, variantId, region).catch(() => null)
        : null;

    if (lastSmartCartIncreaseIntent !== intent) return;
    clearSmartCartIncreaseIntent();

    const cappedQty = Math.max(
      0,
      Number(availability?.ok === true ? availability.quantity : currentQty)
    );
    if (currentQty <= intent.quantityBefore && cappedQty <= currentQty) {
      updateSmartCartBanner(
        true,
        smartCart,
        buildQuantityLimitedMessage(currentQty, intent.productTitle)
      );
    }
  }

  function captureSmartCartManualQuantityIntent(target) {
    if (!target || !target.closest) return null;
    const input = target.closest(
      "#rebuy-cart .rebuy-cart__flyout-item-quantity-widget-label"
    );
    if (!input) return null;
    const itemEl = input.closest(".rebuy-cart__flyout-item");
    if (!itemEl) return null;
    const itemEls = Array.from(
      document.querySelectorAll("#rebuy-cart .rebuy-cart__flyout-item")
    );
    const rowIndex = itemEls.indexOf(itemEl);
    if (rowIndex < 0) return null;
    const smartCart = getSmartCart();
    const items = Array.isArray(smartCart?.items?.()) ? smartCart.items() : [];
    const item = items[rowIndex];
    const variantId = getSmartCartVariantId(item);
    if (!variantId) return null;
    const quantityBefore = Math.max(0, Number(item?.quantity || 0));
    const desiredQty = Math.max(0, Number(input.value || input.getAttribute("value") || 0));
    const productTitle = normalizeWhitespace(
      itemEl.querySelector(".rebuy-cart__flyout-item-product-title")?.textContent || ""
    );
    if (!Number.isFinite(desiredQty) || desiredQty === quantityBefore) return null;
    return {
      rowIndex,
      variantId,
      productTitle,
      quantityBefore,
      desiredQty,
      clickedAt: Date.now(),
    };
  }

  function scheduleSmartCartQuantityIntentVerification(intent) {
    if (!intent) return;
    lastSmartCartIncreaseIntent = intent;
    if (smartCartIncreaseIntentTimer) {
      clearTimeout(smartCartIncreaseIntentTimer);
    }
    smartCartIncreaseIntentTimer = setTimeout(() => {
      verifySmartCartIncreaseIntent();
    }, 900);
  }

  async function handleSmartCartManualQuantityChange(target) {
    const intent = captureSmartCartManualQuantityIntent(target);
    if (!intent) return;
    clearSmartCartNotices();
    scheduleSmartCartQuantityIntentVerification(intent);
    const rebuyCart = window.Rebuy?.Cart;
    if (typeof rebuyCart?.changeItem !== "function") return;
    try {
      await runSmartCartAction(() =>
        rebuyCart.changeItem({
          id: intent.variantId,
          quantity: intent.desiredQty,
        })
      );
      scheduleCartUiSync();
      scheduleRun(undefined, "rebuy:smartcart.qty-input");
    } catch (_error) {
      // Reconcile will fail closed on the next run if the cart cannot be synced.
    }
  }

  function normalizeEndpointBase(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (!/^https?:\/\//i.test(raw)) return "";
    return raw.replace(/\/+$/, "");
  }

  function getCurrentRegionSafe() {
    const regionFromDataset = getDatasetRegion();
    const regionFromWindow = getWindowRegion();
    const regionFromStorage = getStorageRegion();
    return regionFromDataset || regionFromWindow || regionFromStorage || null;
  }

  function buildInventoryEndpoint(base) {
    if (!base) return "";
    const trimmed = String(base).trim().replace(/\/+$/, "");
    if (!/^https?:\/\//i.test(trimmed)) return "";

    // If user gives a Netlify functions folder, append the function name.
    // Accept:
    // - https://site/.netlify/functions
    // - https://site/.netlify/functions/inventory
    if (trimmed.includes("/.netlify/functions")) {
      if (trimmed.endsWith("/.netlify/functions")) {
        return `${trimmed}/inventory`;
      }
      // If it already ends in /inventory (or some other function), keep it.
      return trimmed;
    }

    // Non-netlify style: ensure /inventory
    if (trimmed.endsWith("/inventory")) return trimmed;
    return `${trimmed}/inventory`;
  }

  function normalizeRegion(value) {
    if (typeof value !== "string") return null;
    const normalized = value.trim().toLowerCase();
    return normalized === "east" || normalized === "west" ? normalized : null;
  }

  function getDatasetRegion() {
    const docRegion = document.documentElement?.dataset?.ecomificRegion;
    const bodyRegion = document.body?.dataset?.ecomificRegion;
    return normalizeRegion(docRegion) || normalizeRegion(bodyRegion);
  }

  function getWindowRegion() {
    return getStorageRegion();
  }

  function getStorageRegion() {
    const keys = ["ecomific_region"];
    for (const key of keys) {
      try {
        const val = window.localStorage?.getItem?.(key);
        const normalized = normalizeRegion(val);
        if (normalized) return normalized;
      } catch (err) {
        // ignore storage errors
      }
    }

    const cookie = typeof document !== "undefined" ? document.cookie : "";
    if (!cookie) return null;
    for (const key of keys) {
      const match = cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
      const val = match ? decodeURIComponent(match[1]) : null;
      const normalized = normalizeRegion(val);
      if (normalized) return normalized;
    }

    return null;
  }

  function getEndpointBase() {
    const cfg =
      window.ecomificAvailabilityConfig &&
      typeof window.ecomificAvailabilityConfig === "object"
        ? window.ecomificAvailabilityConfig
        : {};
    const globalInventoryEndpoint =
      typeof window.ECOMIFIC_INVENTORY_ENDPOINT_URL === "string"
        ? window.ECOMIFIC_INVENTORY_ENDPOINT_URL
        : "";
    return normalizeEndpointBase(
      cfg.endpointBase || cfg.inventoryApiBase || globalInventoryEndpoint || ""
    );
  }

  function getPanel() {
    return document.querySelector(SELECTORS.panel);
  }

  function getPanelStack() {
    return document.querySelector(SELECTORS.panelStack);
  }

  function setPanelVisibility(visible) {
    const panel = getPanel();
    if (!panel) return;
    if (visible) {
      panel.removeAttribute("hidden");
    } else {
      panel.setAttribute("hidden", "hidden");
    }
  }

  function getMessageConfig() {
    const cfg =
      window.ecomificAvailabilityConfig &&
      typeof window.ecomificAvailabilityConfig === "object"
        ? window.ecomificAvailabilityConfig
        : {};
    const messages =
      cfg.messages && typeof cfg.messages === "object" ? cfg.messages : {};
    return {
      updatedTitle:
        String(messages.updatedTitle || "").trim() || "Cart updated",
      errorTitle:
        String(messages.errorTitle || "").trim() || "Cart needs review",
      inventoryAdjusted:
        String(messages.inventoryAdjusted || "").trim() || CART_UPDATED_MESSAGE,
      cartEmptied:
        String(messages.cartEmptied || "").trim() || CART_EMPTIED_MESSAGE,
      unavailable:
        String(messages.unavailable || "").trim() ||
        "This item is not available based on current inventory for your shopping region.",
      quantityLimitedOne:
        String(messages.quantityLimitedOne || "").trim() ||
        "Only [quantity] item available in your region.",
      quantityLimitedOther:
        String(messages.quantityLimitedOther || "").trim() ||
        "Only [quantity] items available in your region.",
      quantityLimitedSuffix:
        String(messages.quantityLimitedSuffix || "").trim() ||
        "available in your region.",
      removedSuffix:
        String(messages.removedSuffix || "").trim() || REMOVED_REASON,
      removedItem:
        String(messages.removedItem || "").trim() ||
        "Removed: [title] (was [quantity]) — [suffix]",
      quantityUpdated:
        String(messages.quantityUpdated || "").trim() ||
        "Quantity updated: was [before], now [after] — [message]",
      removedHeading:
        String(messages.removedHeading || "").trim() ||
        "Removed from your cart",
      dismissLabel:
        String(messages.dismissLabel || "").trim() ||
        "Dismiss cart update message",
      errorMessage:
        String(messages.errorMessage || "").trim() || ERROR_MESSAGE,
    };
  }

  function normalizeWhitespace(text) {
    return String(text || "").replace(/\s+/g, " ").trim();
  }

  function fillMessageTemplate(template, replacements) {
    let output = String(template || "");
    Object.entries(replacements || {}).forEach(([key, value]) => {
      output = output.replaceAll(`[${key}]`, String(value ?? ""));
      output = output.replaceAll(`{{ ${key} }}`, String(value ?? ""));
    });
    return normalizeWhitespace(output);
  }

  function buildQuantityLimitedMessage(quantity, productTitle) {
    const count = Math.max(0, Number(quantity || 0));
    const config = getMessageConfig();
    const base = fillMessageTemplate(
      count === 1 ? config.quantityLimitedOne : config.quantityLimitedOther,
      { quantity: count }
    );
    const name = normalizeWhitespace(productTitle || "");
    return name ? `${name}: ${base}` : base;
  }

  function buildRemovedItemText(item) {
    const config = getMessageConfig();
    return fillMessageTemplate(
      config.removedItem,
      {
        title: item.title,
        quantity: item.beforeQty,
        suffix: config.removedSuffix,
      }
    );
  }

  function createNoticeElement(opts) {
    const title = String(opts?.title || "").trim();
    const message = String(opts?.message || "").trim();
    const removedItems = Array.isArray(opts?.removedItems) ? opts.removedItems : [];
    const dismissible = opts?.dismissible !== false;
    const blocking = opts?.blocking === true;
    const noticeId = String(opts?.id || "");

    const notice = document.createElement("div");
    notice.className = "ecomific-cart-notice";
    if (blocking) {
      notice.classList.add("ecomific-cart-notice--blocking");
      notice.dataset.ecomificNoticeBlocking = "1";
    }
    if (noticeId) {
      notice.dataset.ecomificNoticeId = noticeId;
    }

    const header = document.createElement("div");
    header.className = "ecomific-cart-notice__header";

    const titleWrap = document.createElement("p");
    const titleStrong = document.createElement("strong");
    titleStrong.textContent = title;
    titleWrap.appendChild(titleStrong);
    header.appendChild(titleWrap);

    if (dismissible) {
      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "ecomific-cart-notice__close";
      closeButton.setAttribute("aria-label", getMessageConfig().dismissLabel);
      closeButton.setAttribute("data-ecomific-notice-close", "true");
      closeButton.textContent = "×";
      header.appendChild(closeButton);
    }

    notice.appendChild(header);

    if (message) {
      const messageEl = document.createElement("p");
      messageEl.className = "ecomific-cart-notice__message";
      messageEl.textContent = message;
      notice.appendChild(messageEl);
    }

    if (removedItems.length > 0) {
      const wrap = document.createElement("div");
      wrap.className = "ecomific-cart-notice__removed";
      const heading = document.createElement("p");
      const headingStrong = document.createElement("strong");
      headingStrong.textContent = getMessageConfig().removedHeading;
      heading.appendChild(headingStrong);
      wrap.appendChild(heading);
      const list = document.createElement("ul");
      list.className = "list-unstyled";
      for (const entry of removedItems) {
        const li = document.createElement("li");
        li.textContent = entry.text;
        list.appendChild(li);
      }
      wrap.appendChild(list);
      notice.appendChild(wrap);
    }

    return notice;
  }

  function isEquivalentNotice(existing, incoming) {
    if (!existing || !incoming) return false;
    return (
      String(existing.title || "") === String(incoming.title || "") &&
      String(existing.message || "") === String(incoming.message || "") &&
      String(existing.blocking === true) === String(incoming.blocking === true)
    );
  }

  function syncPanelVisibility() {
    const stack = getPanelStack();
    setPanelVisibility(!!(stack && stack.children.length > 0));
  }

  function clearPanelNotices(opts) {
    const stack = getPanelStack();
    if (!stack) return;
    const notices = Array.from(stack.children);
    for (const notice of notices) {
      if (opts?.blockingOnly && notice.dataset.ecomificNoticeBlocking !== "1") {
        continue;
      }
      notice.remove();
    }
    syncPanelVisibility();
  }

  function pushPanelNotice(opts) {
    const stack = getPanelStack();
    if (!stack) return null;
    const normalized = {
      title: opts?.title || getMessageConfig().updatedTitle,
      message: opts?.message || getMessageConfig().inventoryAdjusted,
      removedItems: Array.isArray(opts?.removedItems) ? opts.removedItems : [],
      dismissible: opts?.dismissible !== false,
      blocking: opts?.blocking === true,
    };
    if (opts?.replaceAll) {
      stack.innerHTML = "";
    } else {
      const first = stack.firstElementChild;
      if (
        first &&
        isEquivalentNotice(
          {
            title:
              first.querySelector(".ecomific-cart-notice__header strong")
                ?.textContent || "",
            message:
              first.querySelector(".ecomific-cart-notice__message")?.textContent ||
              "",
            blocking: first.dataset.ecomificNoticeBlocking === "1",
          },
          normalized
        )
      ) {
        return first;
      }
    }
    const notice = createNoticeElement(normalized);
    stack.prepend(notice);
    syncPanelVisibility();
    return notice;
  }

  function removeLegacyFixButton() {
    document
      .querySelectorAll("[data-ecomific-cart-reconcile-fix]")
      .forEach((el) => el.remove());
  }

  function setCheckoutBlocked(blocked) {
    const checkoutBtn = document.querySelector(SELECTORS.checkoutButton);
    if (!checkoutBtn) return;

    if (blocked) {
      if (checkoutBtn.dataset[CHECKOUT_BLOCKED_ATTR] !== "1") {
        checkoutBtn.dataset[CHECKOUT_BLOCKED_ATTR] = "1";
        checkoutBtn.dataset[CHECKOUT_PREV_DISABLED_ATTR] = checkoutBtn.disabled
          ? "1"
          : "0";
      }
      checkoutBtn.disabled = true;
      checkoutBtn.setAttribute("aria-disabled", "true");
    } else if (checkoutBtn.dataset[CHECKOUT_BLOCKED_ATTR] === "1") {
      const wasDisabled = checkoutBtn.dataset[CHECKOUT_PREV_DISABLED_ATTR] === "1";
      delete checkoutBtn.dataset[CHECKOUT_BLOCKED_ATTR];
      delete checkoutBtn.dataset[CHECKOUT_PREV_DISABLED_ATTR];
      if (!wasDisabled) {
        checkoutBtn.disabled = false;
        checkoutBtn.removeAttribute("aria-disabled");
      }
    }
  }

  function setAcceleratedCheckoutHidden(hidden) {
    const container = document.querySelector(SELECTORS.acceleratedCheckout);
    if (!container) return;
    if (hidden) {
      if (container.dataset[ACCEL_PREV_DISPLAY_ATTR] == null) {
        container.dataset[ACCEL_PREV_DISPLAY_ATTR] = container.style.display || "";
      }
      container.style.display = "none";
    } else if (container.dataset[ACCEL_PREV_DISPLAY_ATTR] != null) {
      container.style.display = container.dataset[ACCEL_PREV_DISPLAY_ATTR];
      delete container.dataset[ACCEL_PREV_DISPLAY_ATTR];
    }
  }

  function failClosed(message) {
    if (panelDeferredClearTimer) {
      clearTimeout(panelDeferredClearTimer);
      panelDeferredClearTimer = null;
    }
    panelMinVisibleUntil = 0;
    pushPanelNotice({
      title: getMessageConfig().errorTitle,
      message: message || getMessageConfig().errorMessage,
      dismissible: false,
      blocking: true,
      replaceAll: true,
    });
    setCheckoutBlocked(true);
    setAcceleratedCheckoutHidden(true);
  }

  function showBanner(message, opts) {
    if (panelDeferredClearTimer) {
      clearTimeout(panelDeferredClearTimer);
      panelDeferredClearTimer = null;
    }
    panelMinVisibleUntil = Date.now() + RECONCILE_MESSAGE_MIN_VISIBLE_MS;
    pushPanelNotice({
      title: getMessageConfig().updatedTitle,
      message: message || getMessageConfig().inventoryAdjusted,
      removedItems: Array.isArray(opts?.removedItems) ? opts.removedItems : [],
      dismissible: true,
      blocking: false,
    });
    setCheckoutBlocked(false);
    setAcceleratedCheckoutHidden(false);
  }

  function clearPanelStateNow(opts) {
    clearPanelNotices({ blockingOnly: !opts?.force });
    setCheckoutBlocked(false);
    setAcceleratedCheckoutHidden(false);
  }

  function getAdjustmentBannerCopy({
    hasRegionMismatch,
    removedItems,
    lineChanges,
    cartWillBeEmpty,
  }) {
    const removedCount = Array.isArray(removedItems) ? removedItems.length : 0;
    const lineChangeCount = Array.isArray(lineChanges) ? lineChanges.length : 0;
    if (cartWillBeEmpty && removedCount > 0) {
      return getMessageConfig().cartEmptied;
    }
    if (!hasRegionMismatch && removedCount === 0 && lineChangeCount === 1) {
      return buildQuantityLimitedMessage(
        lineChanges[0].afterQty,
        lineChanges[0].title
      );
    }
    return getMessageConfig().inventoryAdjusted;
  }

  function clearPanelState() {
    clearPanelStateNow({ force: true });
  }

  function getCartItemTitle(item) {
    const row = document.querySelector(
      `${SELECTORS.cartItemRow}[data-key="${item.key}"]`
    );
    let title = item.product_title || item.title || "Item";

    if (row) {
      const titleEl = row.querySelector(".cart__item-name");
      if (titleEl && titleEl.textContent) {
        title = titleEl.textContent.trim();
      }
    }
    return title;
  }

  function dedupeVariants(cartItems) {
    const map = new Map();
    for (const item of cartItems) {
      const variantId = item?.variant_id;
      if (!variantId) continue;
      const key = String(variantId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return map;
  }

  function clearCartLineNotes() {
    document
      .querySelectorAll("[data-ecomific-cart-line-note]")
      .forEach((el) => el.remove());
  }

  function renderCartLineNotes(changes) {
    if (!changes || changes.length === 0) return;
    for (const change of changes) {
      if (change.afterQty <= 0) continue;
      if (change.afterQty >= change.beforeQty) continue;
      const row = document.querySelector(
        `${SELECTORS.cartItemRow}[data-key="${change.key}"]`
      );
      if (!row) continue;
      const qtyWrapper = row.querySelector(".js-qty__wrapper");
      const removeTarget = row.querySelector(".cart__remove");
      const note = document.createElement("div");
      note.setAttribute("data-ecomific-cart-line-note", "true");
      note.className = "ecomific-cart-line-note";
      note.textContent = fillMessageTemplate(getMessageConfig().quantityUpdated, {
        before: change.beforeQty,
        after: change.afterQty,
        message: buildQuantityLimitedMessage(change.afterQty, change.title),
      });
      if (qtyWrapper && qtyWrapper.parentNode) {
        qtyWrapper.parentNode.insertBefore(note, qtyWrapper.nextSibling);
      } else if (removeTarget && removeTarget.parentNode) {
        removeTarget.parentNode.insertBefore(note, removeTarget);
      }
    }
  }

  function limitConcurrency(list, limit, handler) {
    let index = 0;
    let active = 0;
    const results = [];

    return new Promise((resolve) => {
      const next = () => {
        if (index >= list.length && active === 0) {
          resolve(results);
          return;
        }

        while (active < limit && index < list.length) {
          const item = list[index++];
          active += 1;
          Promise.resolve()
            .then(() => handler(item))
            .then((result) => {
              results.push({ item, result });
            })
            .catch((error) => {
              results.push({ item, error });
            })
            .finally(() => {
              active -= 1;
              next();
            });
        }
      };
      next();
    });
  }

  async function fetchCart(signal) {
    const res = await fetch("/cart.js", { credentials: "same-origin", signal });
    if (!res.ok) {
      throw new Error("Cart fetch failed");
    }
    return res.json();
  }

  function updateHeaderCartBadge(count) {
    const numericCount = Math.max(0, Number(count || 0));
    document.querySelectorAll(".cart-link__bubble-num").forEach((el) => {
      el.textContent = String(numericCount);
    });
    document.querySelectorAll(".cart-link__bubble").forEach((el) => {
      el.classList.toggle("cart-link__bubble--visible", numericCount > 0);
    });
  }

  function dispatchCartUpdated(cart) {
    if (!cart) return;
    try {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
        })
      );
    } catch (err) {
      // ignore event dispatch errors
    }
  }

  function syncHeaderCartUi(cart) {
    if (!cart || typeof cart !== "object") return;
    updateHeaderCartBadge(cart.item_count);
    dispatchCartUpdated(cart);
  }

  async function syncCartUiFromShopify() {
    try {
      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      const cart = await fetchCart(controller ? controller.signal : undefined);
      syncHeaderCartUi(cart);
      return cart;
    } catch (error) {
      dlog("cart-ui:sync:error", { error });
      return null;
    }
  }

  function scheduleCartUiSync(delayMs) {
    if (cartUiSyncDebounceId) {
      clearTimeout(cartUiSyncDebounceId);
    }
    cartUiSyncDebounceId = setTimeout(() => {
      cartUiSyncDebounceId = null;
      syncCartUiFromShopify();
    }, typeof delayMs === "number" ? delayMs : CART_UI_SYNC_DEBOUNCE_MS);
  }

  async function fetchInventory(endpoint, variantId, region, signal) {
    const url = `${endpoint}?variant_id=${encodeURIComponent(
      variantId
    )}&region=${encodeURIComponent(region)}`;
    const res = await fetch(url, { credentials: "omit", signal });
    if (!res.ok) {
      throw new Error("Inventory fetch failed");
    }
    return res.json();
  }

  async function applyFixesCartPage(actions) {
    if (!Array.isArray(actions) || actions.length === 0) {
      logDebug("cart-reconcile: no actions, skipping update");
      return Promise.resolve();
    }
    const sorted = [...actions].sort((a, b) => b.line - a.line);
    for (const action of sorted) {
      const payload = { line: action.line, quantity: action.quantity };
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error("Cart change failed");
      }
      await res.json();
    }
  }

  function getSmartCart() {
    return window.Rebuy && window.Rebuy.SmartCart ? window.Rebuy.SmartCart : null;
  }

  function getSmartCartVariantId(item) {
    if (!item || typeof item !== "object") return null;
    const raw =
      item.variant_id ||
      item.variantId ||
      item.variant?.id ||
      item.product?.selected_variant_id ||
      item.product?.selected_variant?.id;
    return raw != null ? String(raw) : null;
  }

  function runSmartCartAction(fn, timeoutMs = SMARTCART_ACTION_TIMEOUT_MS) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timerId = setTimeout(() => {
        if (settled) return;
        settled = true;
        // Never hang indefinitely if callback-style Rebuy methods do not invoke cb.
        resolve();
      }, Math.max(250, Number(timeoutMs) || SMARTCART_ACTION_TIMEOUT_MS));

      const done = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timerId);
        if (error) reject(error);
        else resolve();
      };

      try {
        const maybePromise = fn(done);
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.then(() => done(), done);
        } else if (fn.length < 1) {
          done();
        }
      } catch (error) {
        done(error);
      }
    });
  }

  async function applyFixesSmartCart(actions) {
    const smartCart = getSmartCart();
    if (!smartCart || !Array.isArray(actions) || actions.length === 0) return;
    const rebuyCart = window.Rebuy?.Cart;
    const hasChangeItem = typeof rebuyCart?.changeItem === "function";

    for (const action of actions) {
      const item = action.item;
      if (!item) continue;
      const targetQty = Math.max(0, Number(action.quantity || 0));
      const variantId = getSmartCartVariantId(item);

      if (hasChangeItem && variantId) {
        await runSmartCartAction(() =>
          rebuyCart.changeItem({
            id: variantId,
            quantity: targetQty,
          })
        );
        continue;
      }

      if (targetQty <= 0) {
        await runSmartCartAction((cb) => smartCart.removeItem(item, cb));
        continue;
      }

      let currentQty = Number(item.quantity || 0);
      while (currentQty > targetQty) {
        await runSmartCartAction((cb) => smartCart.decreaseItem(item, cb));
        currentQty -= 1;
      }
    }
  }

  function getSmartCartVisible(smartCart) {
    if (typeof smartCart?.visible === "boolean") return smartCart.visible;
    return null;
  }

  function nextSmartCartNoticeId() {
    smartCartNoticeId += 1;
    return `smartcart-notice-${smartCartNoticeId}`;
  }

  function ensureSmartCartBannerRoot() {
    const host = document.querySelector("#rebuy-cart .rebuy-cart__flyout-body");
    if (!host) return null;

    let root = document.querySelector("#ecomific-smartcart-adjustment-banner");
    if (!root) {
      root = document.createElement("div");
      root.id = "ecomific-smartcart-adjustment-banner";
      root.className = "ecomific-smartcart-adjustment-banner";
      const stack = document.createElement("div");
      stack.className = "ecomific-smartcart-adjustment-banner__stack";
      root.appendChild(stack);
    }

    const aboveProgress = host.querySelector(
      ".rebuy-cart__progress-bar-container.above"
    );
    const flyoutContent = host.querySelector(".rebuy-cart__flyout-content");
    if (aboveProgress && aboveProgress.parentNode === host) {
      aboveProgress.insertAdjacentElement("afterend", root);
    } else if (flyoutContent && flyoutContent.parentNode === host) {
      host.insertBefore(root, flyoutContent);
    } else if (root.parentNode !== host) {
      host.insertBefore(root, host.firstChild);
    }

    return root;
  }

  function renderSmartCartNotices() {
    const root = ensureSmartCartBannerRoot();
    if (!root) return;
    const stack = root.querySelector(".ecomific-smartcart-adjustment-banner__stack");
    if (!stack) return;
    stack.innerHTML = "";

    if (!smartCartNotices.length) {
      root.remove();
      return;
    }

    for (const notice of smartCartNotices) {
      stack.appendChild(createNoticeElement(notice));
    }
  }

  function pushSmartCartNotice(opts) {
    const normalized = {
      id: nextSmartCartNoticeId(),
      title: opts?.title || getMessageConfig().updatedTitle,
      message: opts?.message || getMessageConfig().inventoryAdjusted,
      removedItems: Array.isArray(opts?.removedItems) ? opts.removedItems : [],
      dismissible: opts?.dismissible !== false,
      blocking: opts?.blocking === true,
    };
    if (isEquivalentNotice(smartCartNotices[0], normalized)) {
      return;
    }
    smartCartNotices.unshift(normalized);
    renderSmartCartNotices();
  }

  function removeSmartCartNoticeById(id) {
    smartCartNotices = smartCartNotices.filter(
      (notice) => String(notice.id) !== String(id)
    );
    renderSmartCartNotices();
  }

  function patchSmartCartVisibilityHooks(smartCart) {
    if (!smartCart || smartCart.__ecomificBannerHooksPatched) return;
    smartCart.__ecomificBannerHooksPatched = true;

    if (typeof smartCart.show === "function") {
      const originalShow = smartCart.show.bind(smartCart);
      smartCart.show = function (...args) {
        const result = originalShow(...args);
        setTimeout(() => {
          renderSmartCartNotices();
        }, 0);
        return result;
      };
    }

    if (typeof smartCart.hide === "function") {
      const originalHide = smartCart.hide.bind(smartCart);
      smartCart.hide = function (...args) {
        clearSmartCartNotices();
        return originalHide(...args);
      };
    }
  }

  function updateSmartCartBanner(show, smartCart, bannerCopy, opts) {
    if (!show) {
      smartCartNotices = [];
      renderSmartCartNotices();
      return;
    }
    pushSmartCartNotice({
      title: getMessageConfig().updatedTitle,
      message: bannerCopy || getMessageConfig().inventoryAdjusted,
      removedItems: Array.isArray(opts?.removedItems) ? opts.removedItems : [],
      dismissible: true,
      blocking: false,
    });
  }

  function showSmartCartTransientMessage(message, durationMs) {
    const bannerCopy = String(message || "").trim() || CART_UPDATED_MESSAGE;
    updateSmartCartBanner(true, getSmartCart(), bannerCopy);
  }

  async function refreshSmartCart(smartCart) {
    try {
      // Pull the real Shopify cart state
      const controller = new AbortController();
      const cart = await fetchCart(controller.signal).catch(() => null);
      if (cart) {
        syncHeaderCartUi(cart);
      }

      // Hydrate SmartCart with the fresh cart (this is the key)
      if (cart && typeof smartCart?.setCart === "function") {
        smartCart.setCart(cart);
      }

      // Recompute any derived state Rebuy uses
      if (typeof smartCart?.enrich === "function") {
        // enrich sometimes returns a promise, sometimes not
        const maybePromise = smartCart.enrich();
        if (maybePromise && typeof maybePromise.then === "function") {
          await maybePromise;
        }
      }

      // Update header counters/subtotals if present
      if (typeof smartCart?.updateCartCount === "function") {
        smartCart.updateCartCount();
      }
      if (typeof smartCart?.updateCartSubtotal === "function") {
        smartCart.updateCartSubtotal();
      }
      if (typeof smartCart?.renderHeader === "function") {
        smartCart.renderHeader();
      }

      // Re-bind/manage DOM if needed
      if (typeof smartCart?.manageElement === "function") {
        const el =
          smartCart?.cartElement ||
          smartCart?.el ||
          document.querySelector("#rebuy-cart") ||
          document.querySelector(".rebuy-cart") ||
          null;

        if (el) smartCart.manageElement(el);
      }

      // Last resort: Vue force update (often non-enumerable, so we probe directly)
      const view = smartCart?.view;
      if (view && typeof view.$forceUpdate === "function") {
        view.$forceUpdate();
      }
      if (view && typeof view.$nextTick === "function") {
        view.$nextTick(() => {});
      }

      dlog("smartcart:refresh:done", {
        hasCart: !!cart,
        used: {
          setCart: typeof smartCart?.setCart === "function",
          enrich: typeof smartCart?.enrich === "function",
          manageElement: typeof smartCart?.manageElement === "function",
          forceUpdate: !!(view && typeof view.$forceUpdate === "function"),
        },
      });
      return cart;
    } catch (err) {
      dlog("smartcart:refresh:error", { err });
      return null;
    }
  }

  function isRecentRegionTrigger() {
    return Date.now() - lastRegionTriggerAt <= REGION_TRIGGER_WINDOW_MS;
  }

  function debugSmartCartState(smartCart, stage) {
    if (!DEBUG_CART_RECONCILE) return;

    try {
      const hasSmartCart = !!smartCart;
      const itemsFn = smartCart && typeof smartCart.items === "function";
      const rawItems = itemsFn ? smartCart.items() : null;
      const items = Array.isArray(rawItems) ? rawItems : [];

      dlog("smartcart:state", {
        stage,
        hasSmartCart,
        itemsFn,
        itemsCount: items.length,
        sample: items.slice(0, 5).map((it) => ({
          variant_id: it?.variant_id,
          variantId: it?.variantId,
          quantity: it?.quantity,
          product_title: it?.product_title || it?.title || it?.productTitle,
        })),
      });

      if (items[0]) {
        dlog("smartcart:item0:keys", { stage, keys: Object.keys(items[0]) });
      }
    } catch (err) {
      dlog("smartcart:state:error", {
        stage,
        message: String(err?.message || err),
      });
    }
  }

  function persistCartAdjustment(payload) {
    try {
      window.sessionStorage?.setItem(
        CART_ADJUSTMENT_SESSION_KEY,
        JSON.stringify(payload)
      );
    } catch (err) {
      // ignore storage errors
    }
  }

  function applyPersistedAdjustment() {
    try {
      if (!getPanel()) return false;
      const raw = window.sessionStorage?.getItem(CART_ADJUSTMENT_SESSION_KEY);
      if (!raw) return false;
      const payload = JSON.parse(raw);
      if (!payload || !payload.bannerCopy) return false;
      window.sessionStorage?.removeItem(CART_ADJUSTMENT_SESSION_KEY);
      const removedItems = Array.isArray(payload.removedItems)
        ? payload.removedItems.map((item) => ({
            text: buildRemovedItemText(item),
          }))
        : [];
      showBanner(payload.bannerCopy, { removedItems });
      const lineChanges = Array.isArray(payload.lineChanges)
        ? payload.lineChanges.map((change) => ({
            key: change.key,
            beforeQty: change.beforeQty,
            afterQty: change.afterQty,
          }))
        : [];
      renderCartLineNotes(lineChanges);
      return true;
    } catch (err) {
      return false;
    }
  }

  async function buildAvailabilityMap(variantIds, endpoint, region, signal) {
    const availabilityMap = new Map();
    if (!variantIds.length) return availabilityMap;

    const results = await limitConcurrency(
      variantIds,
      CONCURRENCY_LIMIT,
      async (variantId) => {
        const payload = await fetchInventory(endpoint, variantId, region, signal);
        return payload;
      }
    );

    for (const { item: variantId, result, error } of results) {
      if (error || !result) {
        availabilityMap.set(variantId, {
          ok: false,
          available: false,
          quantity: 0,
          tracked: null,
          availableForSale: false,
        });
        continue;
      }
      const ok = result.ok === true;
      const available = result.available === true;
      const quantity =
        result.quantity == null ? null : Number(result.quantity || 0);
      const tracked =
        typeof result.tracked === "boolean" ? result.tracked : null;
      const availableForSale = result.available_for_sale === true;
      availabilityMap.set(variantId, {
        ok,
        available,
        quantity,
        tracked,
        availableForSale,
      });
    }

    return availabilityMap;
  }

  function buildCartActionsFromAvailability(cartItems, availabilityMap) {
    const actions = [];
    if (!Array.isArray(cartItems) || cartItems.length === 0) return actions;
    for (let index = 0; index < cartItems.length; index += 1) {
      const item = cartItems[index];
      const variantId = String(item?.variant_id || "");
      if (!variantId) continue;
      const availability = availabilityMap.get(variantId);
      if (!availability || availability.ok !== true) continue;
      let nextQty = Number(item.quantity || 0);
      const usesShopifyNativeAvailability =
        availability.tracked === false && availability.availableForSale === true;
      if (availability.available !== true) {
        nextQty = 0;
      } else if (
        !usesShopifyNativeAvailability &&
        Number.isFinite(availability.quantity) &&
        availability.quantity <= 0
      ) {
        nextQty = 0;
      } else if (
        !usesShopifyNativeAvailability &&
        Number.isFinite(availability.quantity) &&
        nextQty > availability.quantity
      ) {
        nextQty = availability.quantity;
      }
      if (nextQty !== Number(item.quantity || 0)) {
        actions.push({
          line: index + 1,
          quantity: nextQty,
          title: getCartItemTitle(item),
        });
      }
    }
    return actions;
  }

  async function run() {
    const readyRegion = getCurrentRegionSafe();
    if (!readyRegion) {
      logDebug("run skipped (region not ready)");
      return;
    }
    if (runInFlight) {
      pendingRun = true;
      logDebug("run skipped (in-flight), marked pending");
      return;
    }

    runInFlight = true;
    const localRunId = ++runId;
    window.__ecomificCartReconcileLastRun = {
      at: Date.now(),
      stage: "start",
    };
    currentController = typeof AbortController !== "undefined" ? new AbortController() : null;
    const signal = currentController ? currentController.signal : undefined;

    try {
      const form = document.querySelector(SELECTORS.form);
      const smartCart = getSmartCart();
      debugSmartCartState(smartCart, "run:after-getSmartCart");
      patchSmartCartVisibilityHooks(smartCart);
      let cart = null;
      let regionFromCartAttr = null;

      const regionFromDataset = getDatasetRegion();
      const regionFromWindow = getWindowRegion();
      const regionFromStorage = getStorageRegion();

      let region = regionFromDataset || regionFromWindow || regionFromStorage;

      if (!region) {
        const cartForRegion = await fetchCart(signal).catch(() => null);
        regionFromCartAttr = normalizeRegion(cartForRegion?.attributes?.region);
        region = region || regionFromCartAttr;
        if (form) {
          cart = cartForRegion;
        }
      }

      if (form && !cart) {
        cart = await fetchCart(signal);
        regionFromCartAttr = normalizeRegion(cart?.attributes?.region);
        region = region || regionFromCartAttr;
      }

      dlog("region", {
        region,
        sources: {
          dataset: regionFromDataset,
          window: regionFromWindow,
          cartAttr: regionFromCartAttr,
          storage: regionFromStorage,
        },
      });

      const endpointBase = getEndpointBase();
      const endpoint = buildInventoryEndpoint(endpointBase);
      window.__ecomificCartReconcileLastRun = {
        at: Date.now(),
        stage: "region+endpoint",
        region,
        endpoint,
      };
      dlog("endpoint", { endpointBase, endpoint });
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const cartItemCount = cartItems.length;
      if (!initLogged) {
        initLogged = true;
        logInfo("cart reconcile init", { region, endpoint });
      }

      if (localRunId !== runId) return;

      const renderedFromSession = applyPersistedAdjustment();

      if (form && (!endpoint || !region)) {
        dlog("decision", {
          cartItemCount,
          unavailableCount: cartItemCount,
          hasUnknown: true,
          blockCheckout: true,
        });
        clearCartLineNotes();
        failClosed(ERROR_MESSAGE);
        return;
      }

      if (form && cartItemCount === 0) {
        dlog("decision", {
          cartItemCount,
          unavailableCount: 0,
          hasUnknown: false,
          blockCheckout: false,
        });
        clearPanelState();
        clearCartLineNotes();
        return;
      }

      if (form) {
        if (!renderedFromSession) {
          clearCartLineNotes();
        }
        const variantsMap = dedupeVariants(cartItems);
        if (variantsMap.size === 0) {
          dlog("decision", {
            cartItemCount,
            unavailableCount: cartItemCount,
            hasUnknown: true,
            blockCheckout: true,
          });
          failClosed(ERROR_MESSAGE);
          return;
        }

        const variantIds = Array.from(variantsMap.keys());
        const availabilityMap = await buildAvailabilityMap(
          variantIds,
          endpoint,
          region,
          signal
        );

        if (localRunId !== runId) return;

        const normalizedSelectedRegion = normalizeRegion(
          regionFromDataset || regionFromWindow || regionFromStorage
        );
        const normalizedCartRegion = normalizeRegion(regionFromCartAttr);
        const hasRegionMismatch = !!(
          normalizedSelectedRegion &&
          normalizedCartRegion &&
          normalizedSelectedRegion !== normalizedCartRegion
        );

        let hasUnknown = false;
        for (const entry of availabilityMap.values()) {
          if (!entry || entry.ok !== true) {
            hasUnknown = true;
            break;
          }
        }

        if (hasUnknown) {
          dlog("decision", {
            cartItemCount,
            unavailableCount: cartItemCount,
            hasUnknown: true,
            blockCheckout: true,
          });
          failClosed(ERROR_MESSAGE);
          return;
        }

        const actions = [];
        const changes = [];
        for (let index = 0; index < cartItems.length; index += 1) {
          const item = cartItems[index];
          const variantId = String(item?.variant_id || "");
          const availability = availabilityMap.get(variantId);
          if (!availability || availability.ok !== true) {
            hasUnknown = true;
            break;
          }
          const beforeQty = Number(item.quantity || 0);
          let afterQty = beforeQty;
          let reason = "";
          const usesShopifyNativeAvailability =
            availability.tracked === false &&
            availability.availableForSale === true;
          if (availability.available !== true) {
            afterQty = 0;
            reason = REMOVED_REASON;
          } else if (
            !usesShopifyNativeAvailability &&
            Number.isFinite(availability.quantity) &&
            availability.quantity <= 0
          ) {
            afterQty = 0;
            reason = REMOVED_REASON;
          } else if (
            !usesShopifyNativeAvailability &&
            Number.isFinite(availability.quantity) &&
            beforeQty > availability.quantity
          ) {
            afterQty = availability.quantity;
            reason = `${REDUCED_REASON_PREFIX}${afterQty}${REDUCED_REASON_SUFFIX}`;
          }
          if (afterQty !== beforeQty) {
            actions.push({
              line: index + 1,
              quantity: afterQty,
              title: getCartItemTitle(item),
            });
            changes.push({
              key: item.key,
              title: getCartItemTitle(item),
              beforeQty,
              afterQty,
              reason,
            });
          }
        }
        logDebug("cart-reconcile: action count", actions.length);

        if (hasUnknown) {
          dlog("decision", {
            cartItemCount,
            unavailableCount: cartItemCount,
            hasUnknown: true,
            blockCheckout: true,
          });
          failClosed(ERROR_MESSAGE);
          return;
        }

        if (actions.length > 0) {
          lastCartActions = actions;
          dlog("decision", {
            cartItemCount,
            unavailableCount: actions.length,
            hasUnknown: false,
            blockCheckout: false,
          });
          try {
            setCheckoutBlocked(true);
            setAcceleratedCheckoutHidden(true);
            await applyFixesCartPage(actions);
            lastCartActions = [];
            const removedItems = changes
              .filter((change) => change.afterQty <= 0)
              .map((change) => ({
                title: change.title,
                beforeQty: change.beforeQty,
              }));
            const lineChanges = changes
              .filter(
                (change) => change.afterQty > 0 && change.afterQty < change.beforeQty
              )
              .map((change) => ({
                key: change.key,
                title: change.title,
                beforeQty: change.beforeQty,
                afterQty: change.afterQty,
              }));
            const changeMap = new Map(
              changes.map((change) => [String(change.key || ""), change])
            );
            const cartWillBeEmpty = cartItems.every((item) => {
              const key = String(item?.key || "");
              const change = changeMap.get(key);
              if (change) {
                return Number(change.afterQty || 0) <= 0;
              }
              return Number(item?.quantity || 0) <= 0;
            });
            const bannerCopy = getAdjustmentBannerCopy({
              hasRegionMismatch,
              removedItems,
              lineChanges,
              cartWillBeEmpty,
            });
            persistCartAdjustment({
              bannerCopy,
              removedItems,
              lineChanges,
              ts: Date.now(),
            });
            window.location.reload();
            return;
          } catch (error) {
            failClosed(ERROR_MESSAGE);
          }
        } else {
          lastCartActions = [];
          dlog("decision", {
            cartItemCount,
            unavailableCount: 0,
            hasUnknown: false,
            blockCheckout: false,
          });
          if (!renderedFromSession) {
            clearPanelState();
            clearCartLineNotes();
          }
        }
      }

      if (smartCart && endpoint && region) {
        debugSmartCartState(smartCart, "run:enter-smartcart-block");
        const items = Array.isArray(smartCart.items?.()) ? smartCart.items() : [];
        dlog("smartcart:items:resolved", {
          count: items.length,
          endpoint,
          region,
        });
        const smartVariants = [];
        for (const item of items) {
          const variantId =
            item?.variant_id ||
            item?.variantId ||
            item?.variant?.id ||
            item?.variantIdNumeric;
          if (variantId) smartVariants.push(String(variantId));
        }
        const smartVariantIds = Array.from(new Set(smartVariants));
        dlog("smartcart:variants:deduped", { smartVariantIds });
        const availabilityMap = await buildAvailabilityMap(
          smartVariantIds,
          endpoint,
          region,
          signal
        );

        if (localRunId !== runId) return;

        let hasUnknown = false;
        for (const entry of availabilityMap.values()) {
          if (!entry || entry.ok !== true) {
            hasUnknown = true;
            break;
          }
        }
        if (hasUnknown) {
          dlog("smartcart:unknown");
          return;
        }

        const actions = [];
        for (const item of items) {
          const variantId =
            item?.variant_id ||
            item?.variantId ||
            item?.variant?.id ||
            item?.variantIdNumeric;
          if (!variantId) continue;
          const availability = availabilityMap.get(String(variantId));
          if (!availability || availability.ok !== true) continue;
          const usesShopifyNativeAvailability =
            availability.tracked === false &&
            availability.availableForSale === true;
          if (
            availability.available !== true ||
            (!usesShopifyNativeAvailability &&
              Number.isFinite(availability.quantity) &&
              availability.quantity <= 0)
          ) {
            actions.push({
              item,
              quantity: 0,
            });
            continue;
          }
          if (
            !usesShopifyNativeAvailability &&
            Number.isFinite(availability.quantity) &&
            Number(item.quantity || 0) > availability.quantity
          ) {
            actions.push({
              item,
              quantity: availability.quantity,
            });
          }
        }

        logDebug("cart-reconcile: action count", actions.length);
        dlog("smartcart:actions", {
          count: actions.length,
          actions: actions.map((a) => ({
            variant_id: a?.item?.variant_id,
            variantId: a?.item?.variantId,
            beforeQty: a?.item?.quantity,
            afterQty: a?.quantity,
          })),
        });

        if (actions.length > 0) {
          clearSmartCartIncreaseIntent();
          if (
            window.localStorage?.getItem?.("ecomific_debug_verbose") === "1"
          ) {
            dlog("smartcart:actions:before", {
              cartQty: items.map((item) => ({
                variant_id: item?.variant_id || item?.variantId,
                qty: item?.quantity,
              }))
            });
          }
          const regionTriggered = isRecentRegionTrigger();
          const smartChanges = actions.map((action) => {
            const item = action.item || {};
            const beforeQty = Number(item.quantity || 0);
            const afterQty = Math.max(0, Number(action.quantity || 0));
            return {
              title: item.product_title || item.title || item.productTitle || "Item",
              beforeQty,
              afterQty,
            };
          });
          const removedItems = smartChanges
            .filter((change) => change.afterQty <= 0)
            .map((change) => ({
              title: change.title,
              beforeQty: change.beforeQty,
            }));
          const lineChanges = smartChanges
            .filter(
              (change) => change.afterQty > 0 && change.afterQty < change.beforeQty
            )
            .map((change) => ({
              key: "",
              title: change.title,
              beforeQty: change.beforeQty,
              afterQty: change.afterQty,
            }));
          const actionMap = new Map(
            actions.map((action) => [
              String(getSmartCartVariantId(action.item) || ""),
              Math.max(0, Number(action.quantity || 0)),
            ])
          );
          const cartWillBeEmpty = items.every((item) => {
            const variantId = String(getSmartCartVariantId(item) || "");
            if (actionMap.has(variantId)) {
              return Number(actionMap.get(variantId) || 0) <= 0;
            }
            return Math.max(0, Number(item?.quantity || 0)) <= 0;
          });
          const bannerCopy = getAdjustmentBannerCopy({
            hasRegionMismatch: regionTriggered,
            removedItems,
            lineChanges,
            cartWillBeEmpty,
          });
          await applyFixesSmartCart(actions);
          // Force the drawer to re-read the updated cart and rerender
          await refreshSmartCart(smartCart);
          if (regionTriggered) {
            persistCartAdjustment({
              bannerCopy,
              removedItems,
              lineChanges,
              ts: Date.now(),
            });
            window.location.assign("/cart");
            return;
          } else {
            updateSmartCartBanner(true, smartCart, bannerCopy);
          }
          if (
            window.localStorage?.getItem?.("ecomific_debug_verbose") === "1"
          ) {
            const controller = new AbortController();
            const afterCart = await fetchCart(controller.signal).catch(() => null);
            const afterItems = Array.isArray(afterCart?.items)
              ? afterCart.items
              : [];
            dlog("smartcart:actions:after", {
              cartQty: afterItems.map((item) => ({
                variant_id: item?.variant_id,
                qty: item?.quantity,
              })),
            });
          }
          scheduleCartUiSync(0);
          scheduleRun(RUN_DEBOUNCE_AFTER_ADJUST_MS);
        } else {
          dlog("smartcart:truth", {
            cartQty: items.map((item) => ({
              variant_id: item?.variant_id || item?.variantId,
              qty: item?.quantity,
            })),
            availability: Array.from(availabilityMap.entries()).map(
              ([id, availability]) => ({
                id,
                ok: availability?.ok,
                available: availability?.available,
                quantity: availability?.quantity,
              })
            ),
            reason: "no-diff",
            actionCount: actions.length,
          });
        }
      }
    } catch (error) {
      if (signal && signal.aborted) return;
      if (localRunId !== runId) return;
      clearCartLineNotes();
      failClosed(ERROR_MESSAGE);
    } finally {
      runInFlight = false;
      if (pendingRun) {
        pendingRun = false;
        scheduleRun();
      }
    }
  }

  function scheduleRun(delayMs, reason) {
    if (typeof reason === "string" && reason) {
      if (reason.indexOf("region:") === 0) {
        lastRegionTriggerAt = Date.now();
      }
    }
    if (debounceId) {
      clearTimeout(debounceId);
    }
    debounceId = setTimeout(() => {
      debounceId = null;
      run();
    }, typeof delayMs === "number" ? delayMs : RUN_DEBOUNCE_MS);
  }

  function bindTriggers() {
    window.addEventListener("ecomific:region:ready", () =>
      scheduleRun(undefined, "region:ready")
    );
    window.addEventListener("ecomific:region:changed", () =>
      scheduleRun(undefined, "region:changed")
    );
    window.addEventListener("pageshow", () => scheduleRun(undefined, "pageshow"));

    const form = document.querySelector(SELECTORS.form);
    if (form) {
      form.addEventListener("click", (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        if (target.closest(".js-qty__adjust") || target.closest(SELECTORS.removeLink)) {
          captureCartNoticeContext(target);
          clearPanelStateNow({ force: true });
        }
        if (target.closest(".js-qty__adjust")) {
          scheduleRun(RUN_DEBOUNCE_AFTER_ADJUST_MS, "cart:qty-adjust");
        }
      });

      form.addEventListener("change", (event) => {
        const target = event.target;
        if (target && target.matches && target.matches("input.js-qty__num")) {
          captureCartNoticeContext(target);
          clearPanelStateNow({ force: true });
          scheduleRun(undefined, "cart:qty-input");
        }
      });
    }

    document.addEventListener("rebuy.ready", () =>
      scheduleRun(undefined, "rebuy:ready")
    );
    document.addEventListener("rebuy:smartcart.ready", () =>
      scheduleRun(undefined, "rebuy:smartcart.ready")
    );
    document.addEventListener("rebuy:cart.change", () => {
      scheduleCartUiSync();
      scheduleRun(undefined, "rebuy:cart.change");
    });
    document.addEventListener("rebuy:smartcart.show", () =>
      scheduleRun(undefined, "rebuy:smartcart.show")
    );
    document.addEventListener("rebuy:smartcart.line-item-increase", () =>
      scheduleRun(undefined, "rebuy:smartcart.line-item-increase")
    );
    document.addEventListener("rebuy:smartcart.line-item-decrease", () =>
      scheduleRun(undefined, "rebuy:smartcart.line-item-decrease")
    );
    document.addEventListener("rebuy:smartcart.line-item-remove", () =>
      scheduleRun(undefined, "rebuy:smartcart.line-item-remove")
    );

    if (!smartCartObserverBound) {
      smartCartObserverBound = true;
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== "childList") continue;
          const target = mutation.target;
          if (!target || !target.closest) continue;
          const isFlyout =
            target.closest("#rebuy-cart .rebuy-cart__flyout-items") ||
            target.closest("#rebuy-cart .rebuy-cart__flyout-body");
          if (!isFlyout) continue;
          if ((mutation.addedNodes?.length || 0) > 0) {
            dlog("smartcart:observer:mutation", {
              addedNodes: mutation.addedNodes ? mutation.addedNodes.length : 0,
              target: mutation.target?.nodeName,
            });
            scheduleRun(undefined, "rebuy:observer-mutation");
            break;
          }
        }
      });
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  function init() {
    // Allow SmartCart reconcile on any page where Rebuy drawer can appear.
    const isCartPage =
      !!(window.location && window.location.pathname === "/cart");
    const panel = getPanel();

    removeLegacyFixButton();
    if (!panelCloseBound) {
      panelCloseBound = true;
      document.addEventListener("click", (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const smartCartControl = target.closest(
          '#rebuy-cart .rebuy-cart__flyout-item-quantity-widget-button, #rebuy-cart .rebuy-button, #rebuy-cart .rebuy-cart__flyout-item-remove, #rebuy-cart .rebuy-cart__flyout-close'
        );
        if (smartCartControl && !target.closest(SELECTORS.noticeClose)) {
          clearSmartCartNotices();
        }
      });
      document.addEventListener("click", (event) => {
        captureSmartCartIncreaseIntent(event.target);
      });
      document.addEventListener(
        "focusout",
        (event) => {
          const target = event.target;
          if (!target || !target.closest) return;
          if (
            target.closest(
              "#rebuy-cart .rebuy-cart__flyout-item-quantity-widget-label"
            )
          ) {
            handleSmartCartManualQuantityChange(target);
          }
        },
        true
      );
      document.addEventListener("keydown", (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        if (
          event.key === "Enter" &&
          target.closest(
            "#rebuy-cart .rebuy-cart__flyout-item-quantity-widget-label"
          )
        ) {
          event.preventDefault();
          handleSmartCartManualQuantityChange(target);
          if (typeof target.blur === "function") target.blur();
        }
      });
      document.addEventListener("click", (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const closeButton = target.closest(SELECTORS.noticeClose);
        if (!closeButton) return;
        const notice = closeButton.closest(".ecomific-cart-notice");
        if (!notice) return;
        const noticeId = notice.dataset.ecomificNoticeId;
        if (noticeId) {
          removeSmartCartNoticeById(noticeId);
        } else {
          notice.remove();
          syncPanelVisibility();
        }
      });
    }

    // Patch browser quantity/stock alerts globally (cart + drawer/quick-add paths).
    patchCartAlert();

    if (DEBUG_CART_RECONCILE) patchCartMutationSniffer();

    // Bind triggers globally (region changes + smartcart open, etc.)
    bindTriggers();

    // If we're on /cart but the panel is missing, there's nothing meaningful to render.
    // Still keep SmartCart logic alive on non-cart pages.
    if (isCartPage && !panel) return;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.ecomificCartReconcile = {
    run,
    scheduleRun,
    updateSmartCartBanner,
  };
})();
